import React from 'react'

import LockIcon from '@mui/icons-material/Lock'
import { Button, InputAdornment, Typography, useTheme } from '@mui/material'
import { Box } from '@mui/system'
import { TopicType } from 'constants/enums/TopicTypeEnum'
import { countWords } from 'utils/countWords'

import { BaseTextField } from 'components/Form'
import TopicChips from 'components/Hero/TopicChips'
import TopicRefinementSection from 'components/TopicRefinementSection'
import WarningMessage from 'components/WarningMessage/WarningMessage'

import { styles } from './Section1.styles'

export const TEST_ID = 'building-blocks-section1'

export type Props = {
	companyProblem: string
	createPrompt: () => void
	editable: boolean
	generateScenarioDisabled: () => boolean
	innovationCompany: string
	innovationTopic: TopicType
	isLoading: boolean
	resetAllField: () => void
	setCompanyProblem: (val: string) => void
	setInnovationCompany: (val: string) => void
	setInnovationTopic: (val: TopicType) => void
}

const Section1 = ({
	innovationCompany,
	setInnovationCompany,
	innovationTopic,
	setInnovationTopic,
	companyProblem,
	setCompanyProblem,
	generateScenarioDisabled,
	resetAllField,
	isLoading,
	createPrompt,
	editable,
}: Props) => {
	const theme = useTheme()

	React.useEffect(() => {
		// Load data from localStorage on component mount
		const storedCompany = localStorage.getItem('innovationCompany')
		const storedTopic = localStorage.getItem('innovationTopic')
		const storedProblem = localStorage.getItem('companyProblem')
		if (storedCompany) setInnovationCompany(storedCompany)
		if (storedTopic) setInnovationTopic(storedTopic as TopicType)
		if (storedProblem) setCompanyProblem(storedProblem)
	}, [setInnovationCompany, setInnovationTopic, setCompanyProblem])
	// Save to localStorage whenever values change
	React.useEffect(() => {
		localStorage.setItem('innovationCompany', innovationCompany)
		localStorage.setItem('innovationTopic', innovationTopic)
		localStorage.setItem('companyProblem', companyProblem)
	}, [innovationCompany, innovationTopic, companyProblem])

	return (
		<>
			<Typography sx={styles.typography} variant={'overline'}>
				1. INNOVATION TOPIC
			</Typography>
			<Box display={'flex'}>
				<Box sx={styles.sideBar} />
				<Box
					sx={styles.question1Box}
					data-testid={`${TEST_ID}__company-input-container`}
				>
					<Typography
						color="black"
						data-testid={`${TEST_ID}__company-question`}
					>
						What company are you brainstorming about today?
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
							required: false,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-name-input`}
						label="Enter company name here."
						value={innovationCompany}
						onChangeValue={(val) => setInnovationCompany(val)}
						required
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							...styles.enterCompany,
							...styles.textField,
						}}
					/>
					<Typography
						color="black"
						data-testid={`${TEST_ID}__business-topic-question`}
					>
						What business topic do you want to focus on?
					</Typography>
					<TopicChips
						color={
							theme.palette.chipColor.buildingBlocks
								.main as string
						}
						innovationTopic={innovationTopic}
						setInnovationTopic={setInnovationTopic}
						editable={editable}
					/>
					<Typography
						color="black"
						sx={styles.summarizeBusinessProblem}
						data-testid={`${TEST_ID}__business-problem-question`}
					>
						Enter the problem to solve by finishing this phrase: how
						might we create new ways to...
					</Typography>
					<BaseTextField
						disabled={!editable}
						InputLabelProps={{
							style: styles.inputLabel,
						}}
						variant="outlined"
						data-testid={`${TEST_ID}__company-problem-input`}
						label="Finish the phrase: how might we create new ways to ..."
						onChangeValue={(val) => setCompanyProblem(val)}
						value={companyProblem}
						size="medium"
						InputProps={{
							endAdornment: !editable && (
								<InputAdornment position="start">
									<LockIcon />
								</InputAdornment>
							),
						}}
						sx={{
							...styles.enterProblem,
							...styles.textField,
						}}
					/>
					<WarningMessage
						prompt={companyProblem}
						color="gameBuildingBlocks"
					/>
					<TopicRefinementSection
						businessProblem={companyProblem}
						companyName={innovationCompany}
						dataTestId={TEST_ID}
					/>
					<Box display="flex" sx={styles.buttonFlex} gap="2rem">
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__create-prompt-button`}
							disabled={generateScenarioDisabled() || isLoading}
							onClick={createPrompt}
							aria-describedby={
								countWords(companyProblem) >= 15
									? 'topic-refinement-education'
									: undefined
							}
						>
							START IDEATING
						</Button>
						<Button
							disabled={!editable}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__reset-all-button`}
							onClick={resetAllField}
						>
							RESET ALL FIELDS
						</Button>
					</Box>
				</Box>
			</Box>
		</>
	)
}
export default Section1
